import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query"
import type {AxiosResponse} from "axios"

import {usePaginatedQuery} from "../components/Pagination.tsx"
import {EOrderDirection, TOrderBy} from "../components/Table/utils/shared.ts"
import {TSalesAssistantDealsTableColumn} from "../routes/SalesAssistant/utils.ts"
import api, {queryKey, salesAssistantGeneralKey} from "../services"
import {
  ASalesAssistantDealPlannedTaskTypes,
  ASalesAssistantDealPriorities,
  ASalesAssistantDealsListResult,
  ASalesAssistantDealSortByValues,
  ASalesAssistantDealSortDirectionValues,
  ASalesAssistantSettingsPartialUpdateData,
  ASalesAssistantTaskCreateParams,
  ASalesAssistantTaskParams,
  ASalesAssistantUpdateCenterListResult,
  AUserTypes,
} from "../services/types.generated"
import {useValueByUserType} from "../utils/userTypes.ts"
import {useInfinitePaginatedQuery} from "./general.ts"

function dealsSortColumnToApi(column?: TSalesAssistantDealsTableColumn): ASalesAssistantDealSortByValues | undefined {
  switch (column) {
    case "title":
      return ASalesAssistantDealSortByValues.Title
    case "priority":
      return ASalesAssistantDealSortByValues.Priority
    case "selling_title":
      return ASalesAssistantDealSortByValues.SellingTitle
    default:
      return undefined
  }
}

const directionToApi = (direction: EOrderDirection | undefined): ASalesAssistantDealSortDirectionValues | undefined => {
  switch (direction) {
    case EOrderDirection.ASC:
      return ASalesAssistantDealSortDirectionValues.Asc
    case EOrderDirection.DESC:
      return ASalesAssistantDealSortDirectionValues.Desc
    default:
      return undefined
  }
}

export const useSaaSSalesPersonSalesAssistantDealsQuery = ({
  pageSize,
  ...filter
}: {
  pageSize: number
  orderBy: TOrderBy<TSalesAssistantDealsTableColumn>
  searchString?: string
  priorities?: ASalesAssistantDealPriorities[]
  plannedActivitiesTypes?: ASalesAssistantDealPlannedTaskTypes[]
  salesPersonExternalId?: string
}) => {
  const queryFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantDealsList,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantDealsList,
  })
  return usePaginatedQuery(
    queryKey.salesAssistantDeals(filter),
    // Without dashboard=true the endpoint returns the legacy shape.
    paginationQuery =>
      queryFn({
        sort_by: dealsSortColumnToApi(filter.orderBy?.column),
        sort_direction: directionToApi(filter.orderBy?.direction),
        search: filter.searchString,
        "priorities[]": filter.priorities,
        "planned_task_types[]": filter.plannedActivitiesTypes,
        sales_person_external_id: filter.salesPersonExternalId,
        ...paginationQuery,
      }) as Promise<AxiosResponse<ASalesAssistantDealsListResult>>,
    {pageSize}
  )
}

export const useSalesAssistantDealDetailQuery = ({externalId}: {externalId?: string}) => {
  const queryFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantDealsDetail,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantDealsDetail,
  })

  return useQuery({
    queryKey: queryKey.salesAssistantDealDetail(externalId),
    queryFn: async () => await queryFn(externalId ?? ""),
  })
}

export const useSalesAssistantTasksPartialUpdateMutation = () => {
  const queryClient = useQueryClient()
  const mutationFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantTasksPartialUpdate,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantTasksPartialUpdate,
  })

  return useMutation({
    mutationFn: (task: ASalesAssistantTaskParams) => mutationFn({task}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: salesAssistantGeneralKey,
      })
    },
  })
}

export const useSalesAssistantCreateTaskMutation = () => {
  const queryClient = useQueryClient()
  const mutationFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantTasksCreate,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantTasksCreate,
  })

  return useMutation({
    mutationFn: (task: ASalesAssistantTaskCreateParams) => mutationFn({task}),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: salesAssistantGeneralKey,
      })
    },
  })
}

export const useInfiniteSalesAssistantSalesPeopleQuery = ({
  sellingExternalId,
  pageSize = 10,
}: {
  sellingExternalId?: string
  pageSize?: number
}) => {
  const queryFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantSalesPeopleList,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantSalesPeopleList,
  })

  return useInfinitePaginatedQuery(
    [...salesAssistantGeneralKey, "sales-people-infinite", {sellingExternalId, pageSize}],
    async paginationParams => {
      return queryFn({
        selling_external_id: sellingExternalId,
        ...paginationParams,
      })
    },
    {pageSize}
  )
}

// Large page size so the select box lists everyone in one request — the endpoint returns no pagination
// meta, so we can't page through it.
const SALES_PEOPLE_SELECT_BOX_PAGE_SIZE = 100

/**
 * Sales people for the settings selector dropdown (SaaS company users only). Uses the lightweight
 * `select_box_index` endpoint, which returns basic sales-person data without pagination.
 */
export const useSalesAssistantSalesPeopleSelectBoxQuery = () => {
  return useQuery({
    queryKey: [...salesAssistantGeneralKey, "sales-people-select-box"],
    queryFn: async () =>
      await api.saasCompanyUsers.salesAssistantSalesPeopleSelectBoxIndexList({
        per_page: SALES_PEOPLE_SELECT_BOX_PAGE_SIZE,
      }),
  })
}

export const useInfiniteSalesAssistantUpdateCenterQuery = ({
  search,
  date,
  salesPersonExternalId,
  pageSize = 10,
}: {
  search?: string
  date?: string
  // `salesPersonExternalId` is part of the query key so the cache is partitioned per selected sales
  // person, and it is sent to the API to scope the list (SaaS company users only — see SalesPersonSelect).
  salesPersonExternalId?: string
  pageSize?: number
}) => {
  const queryFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantUpdateCenterList,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantUpdateCenterList,
  })

  return useInfinitePaginatedQuery(
    [...salesAssistantGeneralKey, "update-center-infinite", {search, date, salesPersonExternalId, pageSize}],
    async paginationParams => {
      // Without dashboard=true the endpoint returns the legacy shape.
      return queryFn({
        search,
        date,
        sales_person_external_id: salesPersonExternalId,
        ...paginationParams,
      }) as Promise<AxiosResponse<ASalesAssistantUpdateCenterListResult>>
    },
    {pageSize}
  )
}

export const useSalesAssistantCreateCallMutation = () => {
  const mutationFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantCreateCallCreate,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantCreateCallCreate,
  })

  return useMutation({mutationFn: () => mutationFn()})
}

export const useSalesAssistantSettingsPartialUpdateMutation = () => {
  const queryClient = useQueryClient()
  const mutationFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantSettingsPartialUpdate,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantSettingsPartialUpdate,
  })

  return useMutation({
    mutationFn: (data: ASalesAssistantSettingsPartialUpdateData) => mutationFn(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: salesAssistantGeneralKey,
      })
    },
  })
}

export const useSalesAssistantSettingsListQuery = () => {
  const queryFn = useValueByUserType({
    [AUserTypes.SaasSalesPerson]: api.saasSalesPeople.salesAssistantSettingsList,
    [AUserTypes.SaasCompanyUser]: api.saasCompanyUsers.salesAssistantSettingsList,
  })

  return useQuery({
    queryKey: salesAssistantGeneralKey,
    queryFn: async () => await queryFn(),
  })
}
